export type FlightState = {
  angle: number;
  radius: number;
  altitude: number;
};

export type FlightInput = {
  radiusDir: number;
  altitudeDir: number;
};

export type FlightConfig = {
  centerX: number;
  centerZ: number;
  radiusRange: readonly [number, number];
  altitudeRange: readonly [number, number];
  radiusRate: number;
  altitudeRate: number;
  angularSpeed: number;
  bank: number;
};

export const defaultFlightConfig: FlightConfig = {
  centerX: 0,
  centerZ: 0,
  radiusRange: [100, 350],
  altitudeRange: [120, 350],
  radiusRate: 30,
  altitudeRate: 25,
  angularSpeed: 0.12,
  bank: (30 * Math.PI) / 180,
};

export function initialFlightState(cfg: FlightConfig): FlightState {
  const [rMin, rMax] = cfg.radiusRange;
  const [aMin, aMax] = cfg.altitudeRange;
  return {
    angle: 0,
    radius: (rMin + rMax) / 2,
    altitude: (aMin + aMax) / 2,
  };
}

export function stepOrbit(
  state: FlightState,
  input: FlightInput,
  dt: number,
  cfg: FlightConfig,
): FlightState {
  const angle = state.angle + cfg.angularSpeed * dt;
  const radius = clamp(
    state.radius + input.radiusDir * cfg.radiusRate * dt,
    cfg.radiusRange[0],
    cfg.radiusRange[1],
  );
  const altitude = clamp(
    state.altitude + input.altitudeDir * cfg.altitudeRate * dt,
    cfg.altitudeRange[0],
    cfg.altitudeRange[1],
  );
  return { angle, radius, altitude };
}

export type OrbitTransform = {
  position: { x: number; y: number; z: number };
  heading: number;
  bank: number;
};

export function orbitTransform(state: FlightState, cfg: FlightConfig): OrbitTransform {
  const x = cfg.centerX + state.radius * Math.cos(state.angle);
  const z = cfg.centerZ + state.radius * Math.sin(state.angle);
  const heading = state.angle + Math.PI / 2;
  return {
    position: { x, y: state.altitude, z },
    heading,
    bank: cfg.bank,
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
